import {
  API_ROUTES,
  type BuildExportRequest,
  currentQuarter,
  EXPORT_TARGET_LABELS,
  type ExportSummary,
  type ExportTarget,
  IMPLEMENTED_TARGETS,
} from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Field, Input } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { downloadFile } from '../../shared/api/download.js';
import { sessionQuery } from '../../shared/api/queries.js';
import {
  buildExport,
  exportDownloadPath,
  exportKeys,
  exportReadinessQuery,
  exportsQuery,
} from '../../shared/api/export-queries.js';

/**
 * Publishing the books.
 *
 * QuickBooks is a target, never a dependency (ADR 0017). Nothing on this page
 * is required for anything else in the product to work, and no figure shown
 * anywhere else comes from here. An organisation that never opens this screen
 * has complete, correct books.
 *
 * What the screen owes a bookkeeper is honesty about what is going out: what
 * is still a draft, what has changed since it was last sent, and whether the
 * tax rules behind the numbers have ever been checked.
 */

export function ExportPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const queryClient = useQueryClient();

  const [period, setPeriod] = useState(() => currentQuarter(new Date()));
  const [target, setTarget] = useState<ExportTarget>('csv');
  const [error, setError] = useState<string | null>(null);
  const [built, setBuilt] = useState<ExportSummary | null>(null);

  const readiness = useQuery(exportReadinessQuery(organizationId, period.from, period.to));
  const history = useQuery(exportsQuery(organizationId));

  // A plain navigation rather than a fetch: the browser is saving a file, and
  // the session cookie travels with it the same way the QuickBooks download
  // already works.
  const downloadRegister = () => {
    const path = `${API_ROUTES.exports.register(organizationId)}?from=${period.from}&to=${period.to}`;
    downloadFile(path, `receipts-${period.from}-to-${period.to}.csv`).catch((caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'That file could not be downloaded.'),
    );
  };

  const build = useMutation({
    mutationFn: (body: BuildExportRequest) => buildExport(organizationId, body),
    onSuccess: (summary) => {
      setError(null);
      setBuilt(summary);
      void queryClient.invalidateQueries({ queryKey: exportKeys.all(organizationId) });
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'That export could not be built.'),
  });

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-content">Export</h1>
        <p className="mt-1 text-sm text-content-muted">
          A file for your accountant or QuickBooks. These books are the record; this is a copy of
          them.
        </p>
      </div>

      {error !== null ? <Alert tone="danger">{error}</Alert> : null}

      {readiness.data === undefined ? null : readiness.data.notes.length === 0 ? null : (
        <Alert tone="warning" title="Worth knowing before you send this">
          <ul className="list-inside list-disc">
            {readiness.data.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* The register comes first, because it is the one a person reads. The
          QuickBooks file below it is read by QuickBooks. */}
      <Card>
        <CardHeader
          title="The receipt register"
          description="Every receipt in the period as a spreadsheet: date, vendor, description, category, subtotal, tax, total, employee, notes. One row per item, so a fuel stop that also bought a drink lands as two rows with two categories."
        />
        <CardBody className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" onClick={downloadRegister}>
            Download for {period.from} to {period.to}
          </Button>
          <span className="text-xs text-content-muted">
            Opens in Excel or Numbers. Drafts are included and marked, because the point is usually
            to chase what is not finished.
          </span>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Build a file" description="Only records somebody has confirmed." />
        <CardBody className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="From">
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  type="date"
                  value={period.from}
                  onChange={(event) => setPeriod({ ...period, from: event.target.value })}
                />
              )}
            </Field>
            <Field label="To">
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  type="date"
                  value={period.to}
                  onChange={(event) => setPeriod({ ...period, to: event.target.value })}
                />
              )}
            </Field>
            <Field label="Format">
              {({ inputProps }) => (
                <select
                  {...inputProps}
                  value={target}
                  onChange={(event) => setTarget(event.target.value as ExportTarget)}
                  className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
                >
                  {IMPLEMENTED_TARGETS.map((value) => (
                    <option key={value} value={value}>
                      {EXPORT_TARGET_LABELS[value]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>

          {readiness.data === undefined ? null : (
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <Count label="Ready to send" value={readiness.data.readyCount} />
              <Count label="Already sent" value={readiness.data.alreadyExportedCount} />
              <Count label="Changed since sending" value={readiness.data.staleCount} warn />
              <Count label="Still drafts" value={readiness.data.draftCount} warn />
            </div>
          )}

          <div>
            <Button
              onClick={() => build.mutate({ target, from: period.from, to: period.to })}
              isLoading={build.isPending}
            >
              Build the file
            </Button>
          </div>

          {built === null ? null : (
            <Alert tone="success" title={`Version ${built.version} is ready`}>
              <p>
                {built.itemCount - built.skippedCount} record
                {built.itemCount - built.skippedCount === 1 ? '' : 's'} to send
                {built.skippedCount === 0
                  ? '.'
                  : `, and ${built.skippedCount} already sent and unchanged.`}
              </p>
              <p className="mt-2">
                <button
                  type="button"
                  className="font-medium underline"
                  onClick={() => {
                    downloadFile(
                      exportDownloadPath(organizationId, built.id),
                      `${built.target}-${period.from}-to-${period.to}.txt`,
                    ).catch((caught: unknown) =>
                      setError(
                        caught instanceof Error
                          ? caught.message
                          : 'That file could not be downloaded.',
                      ),
                    );
                  }}
                >
                  Download it
                </button>
              </p>
            </Alert>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="What has been sent"
          description="Every batch is kept. It is the only record of what an accountant was handed."
        />
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-content-subtle">
                  <th className="px-3 py-2 font-semibold">Built</th>
                  <th className="px-3 py-2 font-semibold">Period</th>
                  <th className="px-3 py-2 font-semibold">Format</th>
                  <th className="px-3 py-2 font-semibold">Version</th>
                  <th className="px-3 py-2 font-semibold">Records</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {(history.data ?? []).map((summary) => (
                  <tr key={summary.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {summary.requestedAt.slice(0, 10)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {summary.periodStart} → {summary.periodEnd}
                    </td>
                    <td className="px-3 py-2">{summary.target}</td>
                    <td className="tabular px-3 py-2">{summary.version}</td>
                    <td className="tabular px-3 py-2">
                      {summary.itemCount - summary.skippedCount}
                      {summary.skippedCount === 0 ? null : (
                        <span className="text-xs text-content-subtle">
                          {' '}
                          (+{summary.skippedCount} unchanged)
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {summary.status}
                      {summary.warnings.length === 0 ? null : (
                        <span className="block text-xs text-warning">
                          {summary.warnings.length} caveat
                          {summary.warnings.length === 1 ? '' : 's'} recorded
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function Count({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className="rounded-[var(--radius-surface)] border border-border bg-surface px-3 py-2">
      <p className="text-xs text-content-muted">{label}</p>
      <p
        className={
          value > 0 && warn === true
            ? 'tabular text-lg font-semibold text-warning'
            : 'tabular text-lg font-semibold text-content'
        }
      >
        {value}
      </p>
    </div>
  );
}
